import React, { useState, useEffect } from 'react';
import { fetchStats } from '../utils/api';

const LogWindow = () => {
  const [logs, setLogs] = useState({
    lastSynced: null,
    nextUpdate: null,
    relayStatus: {
      connected: false,
      count: 0,
      message: "Checking connection status..."
    },
    moscowTimeProvider: null
  });
  
  const [countdown, setCountdown] = useState('');
  
  useEffect(() => {
    // Function to fetch stats from API
    async function getStats() {
      try {
        const data = await fetchStats();
        
        // Use the nextUpdate time directly from the server instead of calculating it
        const nextUpdateTime = data.nextUpdate ? new Date(data.nextUpdate) : null;
        
        setLogs(prevLogs => ({
          ...prevLogs,
          lastSynced: new Date(data.lastUpdated),
          nextUpdate: nextUpdateTime,
          relayStatus: {
            connected: data.status?.connected || false,
            count: data.connectedRelays || 0,
            message: data.status?.message || (data.connectedRelays > 0 
              ? `Connected to ${data.connectedRelays} relays` 
              : "Not connected to any relays")
          },
          moscowTimeProvider: data.moscowTime?.provider || prevLogs.moscowTimeProvider
        }));
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    }

    // Initial fetch
    getStats();
    
    // Set up polling interval (every 30 seconds)
    const intervalId = setInterval(getStats, 30000);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);
  
  // Add a separate useEffect for the countdown timer that updates every second
  useEffect(() => {
    // Function to update the countdown
    function updateCountdown() {
      if (!logs.nextUpdate) {
        setCountdown('Unknown');
        return;
      }
      
      const now = new Date();
      const diffMs = logs.nextUpdate - now;
      
      if (diffMs <= 0) {
        setCountdown('Due now');
        return;
      }
      
      const diffMins = Math.floor(diffMs / 60000);
      const diffSecs = Math.floor((diffMs % 60000) / 1000);
      
      setCountdown(`${diffMins}m ${diffSecs}s`);
    }
    
    // Update immediately
    updateCountdown();
    
    // Set up interval to update every second
    const countdownInterval = setInterval(updateCountdown, 1000);
    
    // Clean up
    return () => clearInterval(countdownInterval);
  }, [logs.nextUpdate]);
  
  // Format date to readable time
  const formatTime = (date) => {
    if (!date) return 'Unknown';
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="bg-dark-900 border border-primary-700 text-primary-300 p-3 rounded-sm text-xs font-mono terminal-window scanlines">
      <div className="flex justify-between items-center mb-2 border-b border-primary-700 pb-1">
        <h3 className="text-sm font-pixel text-primary-400">SYSTEM LOG</h3>
        <div className="flex space-x-1">
          <div className={`w-2 h-2 rounded-sm ${logs.relayStatus.connected ? 'bg-success-500 animate-pulse-slow' : 'bg-danger-500'}`}></div>
          <div className="w-2 h-2 rounded-sm bg-warning-500 animate-blink"></div>
        </div>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-accent-400">[RELAY_STATUS]</span>
          <span className={logs.relayStatus.connected ? "text-success-400" : "text-danger-400"}>
            {logs.relayStatus.count > 0 
              ? `CONNECTED (${logs.relayStatus.count})` 
              : "DISCONNECTED"}
          </span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-accent-400">[MOSCOW_TIME]</span>
          <span className="text-warning-400">
            {logs.moscowTimeProvider ? logs.moscowTimeProvider.toUpperCase() : "UNKNOWN"}
          </span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-accent-400">[LAST_SYNC]</span>
          <span>{logs.lastSynced ? formatTime(logs.lastSynced) : 'NEVER'}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-accent-400">[NEXT_UPDATE]</span>
          <span className="text-primary-300">
            <span className="mr-2">{logs.nextUpdate ? formatTime(logs.nextUpdate) : 'UNKNOWN'}</span>
            <span className="text-warning-400">{logs.nextUpdate ? `T-${countdown}` : ''}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default LogWindow; 